import React, { useState } from 'react';
import { formatDurationClock } from '../../../utils/duration';
import StoryArtwork from '../../StoryArtwork/StoryArtwork';
import { CloseIcon } from '../../icons';
import { MoreIcon, PauseIcon, PlayIcon } from '../uiIcons';
import { useDesktopLayout } from '../useMediaQuery';
import './Player.css';

export type PlayerMode = 'parentPreview' | 'review' | 'child';
export type PlayerLayout = 'desktop' | 'compact';

export interface PlayerArtwork {
  src?: string | null;
  srcSet?: string | null;
}

export interface PlayerProps {
  mode: PlayerMode;
  title?: string;
  subtitle?: string;
  artwork?: PlayerArtwork | string | null;
  playing: boolean;
  loading?: boolean;
  error?: string | null;
  currentTime: number;
  duration: number;
  onPlay: () => void;
  onPause: () => void;
  /** Seek is only offered where the mode permits it; child mode never seeks. */
  onSeek?: (seconds: number) => void;
  onClose?: () => void;
  /** Compact layout: called by "More playback controls" when supplied. */
  onExpand?: () => void;
  /** Force a layout; otherwise follows the desktop breakpoint. */
  layout?: PlayerLayout;
  className?: string;
  /** Child transport preserves the route-specific accessible name. */
  transportLabel?: string;
  transportTestId?: string;
}

/**
 * Presentational docked transport. It owns no audio element and only calls
 * back. No title means no media, so nothing renders and no space is reserved.
 */
const Player: React.FC<PlayerProps> = ({
  mode,
  title,
  subtitle,
  artwork,
  playing,
  loading = false,
  error,
  currentTime,
  duration,
  onPlay,
  onPause,
  onSeek,
  onClose,
  onExpand,
  layout,
  className,
  transportLabel,
  transportTestId,
}) => {
  const desktop = useDesktopLayout();
  const [expanded, setExpanded] = useState(false);
  if (!title) return null;

  const resolvedLayout: PlayerLayout = layout ?? (desktop ? 'desktop' : 'compact');
  const canSeek = mode !== 'child' && typeof onSeek === 'function';
  const canClose = mode !== 'child' && typeof onClose === 'function';
  const source = typeof artwork === 'string' ? { src: artwork } : artwork || {};
  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const safeTime = Math.min(Math.max(currentTime, 0), safeDuration || Math.max(currentTime, 0));
  const elapsed = formatDurationClock(safeTime);
  const total = formatDurationClock(safeDuration);
  const playLabel = loading ? 'Loading' : playing ? 'Pause' : 'Play';

  const transport = (
    <button
      type="button"
      className="luma-player__play"
      onClick={playing ? onPause : onPlay}
      aria-label={transportLabel || playLabel}
      data-testid={transportTestId}
      aria-busy={loading || undefined}
      disabled={loading}
    >
      {mode === 'child' && loading ? <span className="luma-player__spinner" aria-hidden="true" /> : playing ? <PauseIcon size={mode === 'child' ? 40 : 28} /> : <PlayIcon size={mode === 'child' ? 40 : 28} />}
    </button>
  );

  // Child mode is transport only, with no dock, clock, overflow menu or audio owner.
  if (mode === 'child') return <div className={`luma-player--child${className ? ` ${className}` : ''}`} data-mode="child">{transport}</div>;

  const seek = canSeek ? (
    <div className="luma-player__seek">
      <span className="luma-player__time" aria-hidden="true">{elapsed}</span>
      <input
        type="range"
        className="luma-player__range"
        min={0}
        max={safeDuration || 0}
        step={1}
        value={safeTime}
        onChange={(event) => onSeek?.(Number(event.target.value))}
        aria-label="Playback position"
        aria-valuetext={`${elapsed} of ${total}`}
        disabled={safeDuration === 0}
      />
      <span className="luma-player__time" aria-hidden="true">{total}</span>
    </div>
  ) : null;

  const timeText = (
    <span className="luma-player__time luma-player__time--inline">
      <span className="luma-sr-only">Elapsed </span>
      {elapsed}
      {safeDuration > 0 && <span> of {total}</span>}
    </span>
  );

  const identity = (
    <div className="luma-player__identity">
      <span className="luma-player__art">
        <StoryArtwork src={source.src} srcSet={source.srcSet} alt="" imageClassName="luma-player__image" fallbackClassName="luma-player__fallback" fallbackSize={22} />
      </span>
      <span className="luma-player__text">
        <span className="luma-player__title">{title}</span>
        {subtitle && <span className="luma-player__subtitle">{subtitle}</span>}
        {resolvedLayout === 'compact' && timeText}
      </span>
    </div>
  );

  const showMore = resolvedLayout === 'compact' && (canSeek || typeof onExpand === 'function');
  const expandedOpen = showMore && !onExpand && expanded;

  return (
    <section
      className={`luma-player luma-player--${resolvedLayout} luma-player--${mode}${className ? ` ${className}` : ''}`}
      aria-label="Now playing"
      data-mode={mode}
      data-layout={resolvedLayout}
    >
      <div className="luma-player__bar">
        {identity}
        {resolvedLayout === 'desktop' ? (
          <div className="luma-player__center">
            {transport}
            {seek ?? timeText}
          </div>
        ) : (
          <div className="luma-player__center">{transport}</div>
        )}
        <div className="luma-player__end">
          {showMore && (
            <button
              type="button"
              className="luma-player__more"
              aria-label="More playback controls"
              aria-expanded={onExpand ? undefined : expanded}
              onClick={() => (onExpand ? onExpand() : setExpanded((value) => !value))}
            >
              <MoreIcon size={24} />
            </button>
          )}
          {canClose && (
            <button type="button" className="luma-player__close" aria-label="Close player" onClick={onClose}>
              <CloseIcon size={22} />
            </button>
          )}
        </div>
      </div>
      {expandedOpen && <div className="luma-player__expanded">{seek}</div>}
      {error && (
        <p className="luma-player__error" role="status">{error}</p>
      )}
    </section>
  );
};

export default Player;
